import { ArrowLeft, Briefcase, Building2, Clock, Mail, MapPin, Phone } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { useUserProfile } from '../../hooks/useUsers';
import type { PersonProjectAllocation } from '../../lib/peopleApi';
import { Avatar } from '../tasks/linear/Avatar';
import { personTaskRow, TaskListRow } from '../tasks/TaskListRow';
import { Skeleton } from '../ui/Skeleton';
import { AvatarWithPresence } from './PresenceDot';

// The roster shows a name; the card shows the workload. Past this many tasks
// the list stops being scannable in a 288px column, and the full profile page
// has the filters for the rest.
const MAX_TASKS = 8;

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="px-1 pb-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

function Fact({ icon, children, href }: { icon: ReactNode; children: ReactNode; href?: string }) {
  const body = (
    <>
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      <span className="min-w-0 truncate">{children}</span>
    </>
  );
  const cls = 'flex items-center gap-2 px-1 py-1 text-[13px] text-foreground';
  return href ? (
    <a href={href} className={`${cls} hover:text-brand-600 hover:underline`}>
      {body}
    </a>
  ) : (
    <div className={cls}>{body}</div>
  );
}

function ProjectRow({ project }: { project: PersonProjectAllocation }) {
  const { t } = useI18n();
  const inner = (
    <>
      <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
        {project.projectName}
        {project.projectCode && (
          <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
            {project.projectCode}
          </span>
        )}
      </span>
      <span className="shrink-0 text-[11px] text-muted-foreground">
        {t('teamChat.profile.projectTasks', { count: project.activeTaskCount })}
      </span>
    </>
  );
  const cls = 'flex items-center gap-2 rounded-md px-1.5 py-1.5';
  return project.projectId ? (
    <Link to={`/projects/${project.projectId}`} className={`${cls} hover:bg-accent`}>
      {inner}
    </Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-3 px-3">
      <Skeleton className="h-16 w-16 rounded-full" />
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-3 w-20" />
      <Skeleton className="mt-4 h-3 w-full" />
      <Skeleton className="h-3 w-4/5" />
      <Skeleton className="h-3 w-3/5" />
    </div>
  );
}

/**
 * Discord's member card, rendered in place of the roster: click a member,
 * see who they are (account + person details) and what they're on (projects
 * derived from their open tasks, then the tasks themselves), with a back
 * arrow to the list. Presence comes from the roster so the dot matches.
 */
export function MemberProfilePanel({
  userId,
  online,
  onBack,
  backLabel,
}: {
  userId: string;
  online: boolean;
  onBack: () => void;
  /** What the arrow leads to: the roster in a group, nothing in a DM. */
  backLabel?: string;
}) {
  const { t, locale } = useI18n();
  const { data: profile, isLoading, isError } = useUserProfile(userId);

  const lastSeen = online
    ? t('teamChat.online')
    : profile?.lastSeenAt
      ? new Date(profile.lastSeenAt).toLocaleString(locale, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : t('teamChat.profile.lastSeenNever');

  const roleKey = profile ? `teamChat.profile.role.${profile.role}` : '';
  const roleLabel = profile ? t(roleKey) : '';
  const subtitle = profile
    ? [profile.person?.title, profile.person?.department].filter(Boolean).join(' · ')
    : '';

  return (
    <aside className="hidden w-72 shrink-0 flex-col overflow-y-auto border-l border-border pb-4 lg:flex">
      {/* No coloured banner: it was the avatar's colour stretched into a
          header, which read as a status bar that meant something. It did not. */}
      <div className="flex shrink-0 items-center px-2 pt-2">
        <button
          type="button"
          onClick={onBack}
          aria-label={backLabel ?? t('teamChat.profile.back')}
          title={backLabel ?? t('teamChat.profile.back')}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <ArrowLeft size={16} />
        </button>
      </div>

      {isLoading && (
        <div className="pt-2">
          <ProfileSkeleton />
        </div>
      )}

      {isError && (
        <p className="px-4 pt-4 text-sm text-muted-foreground">{t('teamChat.profile.loadError')}</p>
      )}

      {profile && (
        <>
          <div className="px-4 pt-1">
            <div className="mb-2 inline-block rounded-full">
              <AvatarWithPresence online={online} ringClassName="ring-background">
                {profile.avatarUrl ? (
                  <img
                    src={profile.avatarUrl}
                    alt={profile.name}
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <Avatar name={profile.name} size={64} />
                )}
              </AvatarWithPresence>
            </div>

            <h2 className="truncate font-heading text-base font-semibold text-foreground">
              {profile.name}
            </h2>
            {subtitle && <p className="truncate text-[13px] text-muted-foreground">{subtitle}</p>}
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-medium text-foreground">
                {roleLabel}
              </span>
              {!profile.isActive && (
                <span className="rounded-full bg-red-500/10 px-2 py-0.5 text-[11px] font-medium text-red-600">
                  {t('teamChat.profile.inactive')}
                </span>
              )}
            </div>
          </div>

          <div className="mx-4 my-3 border-t border-border" />

          <section className="px-3">
            <SectionTitle>{t('teamChat.profile.contact')}</SectionTitle>
            <Fact icon={<Mail size={14} />} href={`mailto:${profile.email}`}>
              {profile.email}
            </Fact>
            {profile.person?.phone && (
              <Fact icon={<Phone size={14} />} href={`tel:${profile.person.phone}`}>
                {profile.person.phone}
              </Fact>
            )}
            {profile.person?.location && (
              <Fact icon={<MapPin size={14} />}>{profile.person.location}</Fact>
            )}
            {profile.person?.department && (
              <Fact icon={<Building2 size={14} />}>{profile.person.department}</Fact>
            )}
          </section>

          <section className="mt-4 px-3">
            <Fact icon={<Briefcase size={14} />}>
              <span className="text-muted-foreground">{t('teamChat.profile.memberSince')}</span>{' '}
              {new Date(profile.person?.startDate ?? profile.createdAt).toLocaleDateString(locale, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </Fact>
            <Fact icon={<Clock size={14} />}>
              <span className="text-muted-foreground">{t('teamChat.profile.lastSeen')}</span>{' '}
              <span className={online ? 'text-emerald-600' : undefined}>{lastSeen}</span>
            </Fact>
          </section>

          <div className="mx-4 my-3 border-t border-border" />

          <section className="px-3">
            <SectionTitle>
              {t('teamChat.profile.projects')} — {profile.projects.length}
            </SectionTitle>
            {profile.projects.length === 0 ? (
              <p className="px-1 text-[13px] text-muted-foreground">
                {t('teamChat.profile.noProjects')}
              </p>
            ) : (
              profile.projects.map((p) => (
                <ProjectRow key={p.projectId ?? p.projectName} project={p} />
              ))
            )}
          </section>

          <section className="mt-4 px-3">
            <SectionTitle>
              {t('teamChat.profile.activeTasks')} — {profile.stats.totalActive}
            </SectionTitle>
            {(profile.stats.inProgress > 0 || profile.stats.overdue > 0) && (
              <p className="px-1 pb-1.5 text-[11px] text-muted-foreground">
                {[
                  profile.stats.inProgress > 0 &&
                    t('teamChat.profile.inProgress', { count: profile.stats.inProgress }),
                  profile.stats.overdue > 0 &&
                    t('teamChat.profile.overdue', { count: profile.stats.overdue }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
            {profile.tasks.length === 0 ? (
              <p className="px-1 text-[13px] text-muted-foreground">
                {t('teamChat.profile.noTasks')}
              </p>
            ) : (
              <>
                {/* The shared task row, compact: the column is 288px wide. */}
                <ul className="overflow-hidden rounded-md border border-border">
                  {profile.tasks.slice(0, MAX_TASKS).map((task) => (
                    <TaskListRow key={task.id} task={personTaskRow(task, profile.name)} compact />
                  ))}
                </ul>
                {profile.tasks.length > MAX_TASKS && (
                  <p className="px-1.5 pt-1 text-[11px] text-muted-foreground">
                    {t('teamChat.profile.andMore', { count: profile.tasks.length - MAX_TASKS })}
                  </p>
                )}
              </>
            )}
          </section>

          {profile.person && (
            <div className="mt-4 px-3">
              <Link
                to={`/people/${profile.person.id}`}
                className="ui-btn-secondary flex w-full items-center justify-center text-sm"
              >
                {t('teamChat.profile.viewFull')}
              </Link>
            </div>
          )}
        </>
      )}
    </aside>
  );
}
